import { getApiClient } from '@dopomogai/api-client';
import type {
  AssistantReadinessSummary,
  ChatFlowStep,
  ChatInteractionMode,
  ChatWorkspaceMode,
} from '@dopomogai/chat-sdk';

export const DEFAULT_ONBOARDING_SESSION_STORAGE_KEY =
  'dopomogai:agent-builder:onboarding-session-id:v1';

export interface AssistantRuntimeResolveRequest {
  assistant_profile_id?: string | null;
  subject_agent_id?: string | null;
  workspace_mode?: ChatWorkspaceMode | null;
  interaction_mode?: ChatInteractionMode | null;
  flow_step?: ChatFlowStep | null;
  context?: {
    onboarding_form?: {
      form_id?: string;
      surface?: string;
      active_step_id?: string;
    };
    onboarding_session_id?: string | null;
    scan_id?: string | null;
    [key: string]: any;
  };
}

export interface AssistantRuntimeResolveResponse {
  assistant_profile_id?: string | null;
  runtime_agent_id?: string | null;
  subject_agent_id?: string | null;
  subject_context_key?: string | null;
  prompt_config?: {
    turn_prompt_override?: string | null;
    suggested_prompts?: Array<Record<string, any>>;
  } | null;
  readiness_summary?: AssistantReadinessSummary;
  next_best_action?: Record<string, any> | null;
  deeplink_hints?: Array<Record<string, any>>;
  partition_policy?: {
    key_fields?: string[];
    workspace_mode_affects_guidance_only?: boolean;
  } | null;
  resume_conversation_id?: string | null;
}

function createOnboardingSessionId(): string {
  const uuid = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`;
  return `onb_${uuid}`;
}

export function getOrCreateOnboardingSessionId(
  storageKey: string = DEFAULT_ONBOARDING_SESSION_STORAGE_KEY,
): string {
  if (typeof window === 'undefined') {
    return createOnboardingSessionId();
  }

  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }

    const created = createOnboardingSessionId();
    window.sessionStorage.setItem(storageKey, created);
    return created;
  } catch {
    return createOnboardingSessionId();
  }
}

export function composeTurnPromptOverrides(
  ...values: Array<string | null | undefined>
): string | undefined {
  const merged = values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => Boolean(value));

  if (!merged.length) {
    return undefined;
  }

  return Array.from(new Set(merged)).join('\n\n');
}

export async function resolveAssistantRuntime(
  payload: AssistantRuntimeResolveRequest,
  signal?: AbortSignal,
): Promise<AssistantRuntimeResolveResponse | null> {
  try {
    const client = getApiClient();
    const response = await client
      ._axios()
      .post<AssistantRuntimeResolveResponse>('/assistant-runtime/resolve', payload, {
        signal,
      });

    return response.data;
  } catch (error) {
    if (signal?.aborted) {
      return null;
    }

    console.warn(
      '[OnboardingAssistantPanel] assistant runtime resolve failed; falling back to default runtime.',
      error,
    );
    return null;
  }
}
